import React, { Component } from 'react';
import { CircularProgress } from '@mui/material';
import DataVolumeBarChartView from './DataVolumeBarChartView';
import { getCurrentSelectedOption, getNewSelectionValue } from '../rselect/ReactSelectUtils';

import { bytesToString, formatNumber } from 'js/lib/FormatUtils';
import './ChartStyles.scss';
import { DisplayModes } from './chartConstants';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '../errors/ErrorFallback';
import AnalyticsUtils from '../../../lib/AnalyticsUtils';
import { SwitchDataVolumeChart } from 'new/elements/SwitchDataVolumeChart/SwitchDataVolumeChart';
import { PillTabs } from 'new/elements/PillTabs/PillTabs';

const DATE_RANGE_OPTIONS = [
  {
    label: 'Last 30 days',
    value: 30,
  },
  {
    label: 'Last 7 days',
    value: 7,
  },
];

class DataVolumeChartViews extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: DisplayModes.RECORD_COUNT,
    };

    this._onSelectRecordCount = this._onSelectRecordCount.bind(this);
    this._onSelectVolumeCount = this._onSelectVolumeCount.bind(this);
    this._onSelectErrorCount = this._onSelectErrorCount.bind(this);
    this._getYTickFormat = this._getYTickFormat.bind(this);
    this._getYLabelFormat = this._getYLabelFormat.bind(this);
    this._handleDateRangeChanged = this._handleDateRangeChanged.bind(this);
    this._handleOnControlChange = this._handleOnControlChange.bind(this);
  }

  render() {
    const { mode } = this.state;
    const { forDateRange, isFetchingData } = this.props;

    const selectedDateRange = getCurrentSelectedOption(
      DATE_RANGE_OPTIONS,
      forDateRange,
      DATE_RANGE_OPTIONS[0],
    );

    let initialControl = 'volume';
    let yAttribute = 'size';
    let chartTitle = 'Data Volume (bytes)';

    switch (mode) {
      case DisplayModes.RECORD_COUNT:
        initialControl = 'records';
        yAttribute = 'records';
        chartTitle = 'Data Record Count';

        break;
      case DisplayModes.ERROR_COUNT:
        initialControl = 'errors';
        yAttribute = 'errors';
        chartTitle = 'Data Error Count';
        break;

      case DisplayModes.DATA_VOLUME:
      default:
        break;
    }

    return (
      <section className="volume-chart-view">
        <h2>Recent Usage Trends</h2>
        <div className="chart-header">
          <h3 className="chart-title">{chartTitle}</h3>

          <div className="chart-actions">
            <div className="chart-modes">
              <SwitchDataVolumeChart
                initialValue={initialControl}
                onControlChange={this._handleOnControlChange}
              />
            </div>
          </div>

          <div className="chart-actions">
            <div className="date-filters">
              <PillTabs
                tabs={DATE_RANGE_OPTIONS}
                multiple={false}
                onSelectionChange={this._handleDateRangeChanged}
                selected={selectedDateRange?.value}
              />
            </div>
          </div>
        </div>

        {!!isFetchingData && (
          <div className="chart-progress">
            <CircularProgress size={100} thickness={2} />
          </div>
        )}

        {!isFetchingData && (
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, info) => {
              AnalyticsUtils.reportErrorToSentry(error, info);
            }}
          >
            <DataVolumeBarChartView
              mode={mode}
              data={this.props.data}
              forDateRange={forDateRange}
              yAttribute={yAttribute}
              yTickFormat={this._getYTickFormat}
              yLabelFormat={this._getYLabelFormat}
            />
          </ErrorBoundary>
        )}
      </section>
    );
  }

  _onSelectRecordCount() {
    if (this.state.mode !== DisplayModes.RECORD_COUNT) {
      this.setState({
        mode: DisplayModes.RECORD_COUNT,
      });
    }
  }

  _onSelectErrorCount() {
    if (this.state.mode !== DisplayModes.ERROR_COUNT) {
      this.setState({
        mode: DisplayModes.ERROR_COUNT,
      });
    }
  }

  _onSelectVolumeCount() {
    if (this.state.mode !== DisplayModes.DATA_VOLUME) {
      this.setState({
        mode: DisplayModes.DATA_VOLUME,
      });
    }
  }

  _getYTickFormat(d) {
    const { mode } = this.state;
    if (mode === DisplayModes.DATA_VOLUME) {
      return bytesToString(d, '.0');
    } else {
      return formatNumber(d);
    }
  }

  _getYLabelFormat(d) {
    const { mode } = this.state;
    if (mode === DisplayModes.DATA_VOLUME) {
      return bytesToString(d.y, '.1');
    } else {
      return formatNumber(d.y);
    }
  }

  /**
   * @param newSelectionValue number | { label: string, value: string | number }
   * @private
   */
  _handleDateRangeChanged(newSelectionValue) {
    const { onDateRangeChanged } = this.props;
    const newDateRange =
      typeof newSelectionValue === 'number'
        ? newSelectionValue
        : getNewSelectionValue(newSelectionValue, true, 7);
    onDateRangeChanged(newDateRange);
  }

  _handleOnControlChange(value = '') {
    switch (value) {
      case 'records':
        this._onSelectRecordCount();
        break;
      case 'errors':
        this._onSelectErrorCount();
        break;
      case 'volume':
        this._onSelectVolumeCount();
        break;
      default:
        console.info(`You must use a settled value like: records  | errors | volume`);
    }
  }
}

DataVolumeChartViews.defaultProps = {
  isFetchingData: false,
  forDateRange: 7,
  onDateRangeChanged: () => {},
};

export default DataVolumeChartViews;
